import React, { useState, useEffect } from 'react';
import { Box, Button, Modal, TextField, Typography } from '@mui/material';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../Shared/BotNumberDropdown/index.tsx';
import SearchableDropdown from '../Common/SearchableDropdown/SearchableDropdown.tsx';
import {
  CampaignList,
  Form,
  FormData,
  ValidationErrors,
} from '../../../interfaces/Flowbuilder.interface.ts';
import {
  FlowBuilderActions,
  LeadFormActions,
  TemplateActions,
} from '../../../Services/_actions/index.js';
import { useSnackBar } from '../Common/SnackBar/index.tsx';
import { TemplateFilters, TemplateResponse } from '../../../interfaces/Template.interface.ts';
import { botFlowDataAtom, flowsTableStateAtom } from '../../../Services/_states/index.js';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { Flow } from '../../../interfaces/Flowbuilder.interface.ts';
import { generateFilterKey } from '../../../utils/helper.jsx';
import CampaignDropdown from '../../Shared/CampaignDropdown/index.tsx';
import LoadingButton from '../../Shared/Loading/index.tsx';

const CreateEditModal = ({ open, onClose, modalTitle, flowId, cloneFrom }) => {
  const { showSnackBar } = useSnackBar();
  const leadFormActions = LeadFormActions();
  // const [campaignList, setCampaignList] = useState < CampaignList > ({ pages: 0, list: [] });
  const [formList, setFormList] = useState<(Form | any)[]>([]);
  // const [channelList, setChannelList] = useState([]);
  const actions = FlowBuilderActions();
  const [loading, setLoading] = useState(false);
  const templateActions = TemplateActions();
  // const [flowListData, setFlowListData] = useState<Flow[]>([]);
  const [tableState, setTableState] = useRecoilState(flowsTableStateAtom);
  const [isloading, setIsLoading] = useState(false);
  const [isTemplateloading, setTemplateIsLoading] = useState(false);
  const [templateList, setTemplateList] = useState<TemplateResponse>({ pages: 0, list: [{}] });
  const [templateFilters, setTemplateFilters] = useState<TemplateFilters>({
    search: '',
    page: 0,
    sortOrder: 'desc',
    sortField: 'created_at',
    category: '',
    botNumber: '',
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    organisation: '',
    phoneNumber: '',
    patterns: '',
    title: '',
  });
  const [formData, setFormData] = useState<FormData>({
    title: '',
    patterns: [],
    tags: [],
    is_published: true,
    is_deleted: false,
    is_default: false,
    waba_id: '',
    phone_number_id: '',
    company_id: '',
    organisation_name: '',
    leads_form_name: '',
    app_name: '',
    campaign_id: '',
    campaign_name: '',
    lead_form_id: '',
    template_name: '',
    template_id: '',
    selectedCampaign: null,
    selectedForm: null,
    selectedOrganisation: null,
    selectedPhoneNumber: null,
    selectedTemplate: null,
    flow_type: 'normal',
  });

  const filterKey = generateFilterKey(tableState.filters);

  const flowTypeOptions = [
    { label: 'Normal Bot', value: 'normal' },
    { label: 'Jump Bot', value: 'jump_bot' },
    { label: 'Sorry Bot', value: 'sorry_bot' },
    { label: 'Opt Out', value: 'opt_out' },
    { label: 'Opt In', value: 'opt_in' },
  ];


  const onSelectOrganisation = async (value) => {
    setFormData({
      ...formData,
      company_id: value?.organisation_id || '',
      organisation_name: value?.name || '',
      selectedOrganisation: value,
      selectedForm: null,
      selectedPhoneNumber: null,
      selectedCampaign: null,
      campaign_id: '',
      campaign_name: '',
      lead_form_id: '',
      leads_form_name: '',
      app_name: '',
      phone_number_id: '',
    });
    if (!value) {
      // setCampaignList({ pages: 0, list: [] });
      setFormList([]);
      // setChannelList([]);
      return;
    }
  };

  const checkFormInFlow = async () => {
    if (flowId) {
      let res = await actions.checkFormValidation(formData);
      if (res?.status === 200 && res?.data?.valid) {
        showSnackBar('Please remove parameters from all nodes', 'warning');
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };
  const fetchFormList = async (campaignId) => {
    setFormList([]);
    if (!campaignId) return;
    let res = await leadFormActions.getFormsByCampaignId(campaignId);
    if (res?.status === 200) {
      let data = Object?.values(res?.data);
      setFormList(data || []);
    }
  };

  const onSelectCampaign = async (value) => {
    let checkForm = await checkFormInFlow();
    if (checkForm) {
      setFormData({
        ...formData,
        campaign_id: value?.proposal_id || '',
        campaign_name: value?.name || '',
        selectedCampaign: value,
        selectedForm: null,
        leads_form_name: '',
        lead_form_id: '',
      });
      if (!value) {
        setFormList([]);
        return;
      }
      fetchFormList(value?.proposal_id);
    }
  };

  const handleSearchTemplate = (query) => {
    getTemplateList({ ...templateFilters, search: query || '', page: 0 });
  };

  const onSelectBusinessNumber = async (e) => {
    setFormData({
      ...formData,
      phone_number_id: e?.wa_phone_number || '',
      app_name: e?.app_name,
      selectedPhoneNumber: e || null,
      waba_id: e?.waba_id || '',
    });
    if (e?.wa_phone_number)
      getTemplateList({ ...templateFilters, botNumber: e?.wa_phone_number, page: 0 });
    // else setCampaignList({ pages: 0, list: [] });
  };

  const getTemplateList = async (data) => {
    setTemplateIsLoading(true);
    setTemplateFilters({ ...data });
    try {
      const res = await templateActions.getWhatsappTemplateList(data);
      setTemplateList(res);
    } catch (error) {
      console.warn('Error fetching template list:', error);
    } finally {
      setTemplateIsLoading(false);
    }
  };

  const validateForm = async () => {
    const errors: any = {};

    if (!formData?.title?.trim()) {
      errors.title = 'Flow name is required.';
    }

    if (!formData.patterns.length || formData.patterns[0] === '') {
      errors.patterns = 'At least one keyword is required.';
    }

    if (!formData?.selectedOrganisation) {
      errors.organisation = 'Organization is required.';
    }

    if (!formData?.phone_number_id) {
      errors.phoneNumber = 'Phone Number is required.';
    }

    if (!flowId) {
      if (formData?.title && formData?.company_id && formData?.phone_number_id) {
        try {
          const res: any = await actions.checkFlowName(formData);
          if (res?.length > 0) {
            errors.title = `Flow name "${formData?.title?.trim()}" already exists.`;
          }
        } catch (error) {
          errors.title = 'Error validating flow name. Please try again.';
        }
      }
    }

    if (formData?.company_id && formData?.phone_number_id && formData?.patterns?.length) {
      try {
        const existingPatterns = flowId
          ? tableState?.cache[filterKey]?.find((flow) => flow._id === flowId)?.patterns || []
          : [];

        const newPatterns = formData.patterns.filter(
          (keyword) => !existingPatterns.includes(keyword)
        );

        if (newPatterns.length > 0) {
          const response: any = await actions.checkPatterns(formData, newPatterns);
          if (response?.data?.length > 0) {
            errors.patterns = `Duplicate keywords found: ${response?.data?.join(', ')}`;
          }
        }
      } catch (error) {
        errors.patterns = 'Failed to validate keywords. Please try again.';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateOrUpdateFlow = async () => {
    try {
      const isValid = await validateForm();
      if (!isValid) return;
      setIsLoading(true);
      const {
        selectedCampaign,
        selectedForm,
        selectedOrganisation,
        selectedPhoneNumber,
        selectedTemplate,
        ...updateObj
      }: any = formData;

      let res;

      // if (!flowId) {
      //   if (updateObj?._id) {
      //     updateObj.flow_id = updateObj?._id;
      //     delete updateObj._id;
      //   }
      //   res = await actions.createFlow(updateObj);
      // } else {
      //   res = await actions.updateFlow(flowId, updateObj);
      // }
      const safeUpdateObj = JSON.parse(JSON.stringify(updateObj));

      if (!flowId) {
        if (safeUpdateObj?._id) {
          safeUpdateObj.flow_id = safeUpdateObj?._id;
          delete safeUpdateObj._id;
        }
        res = await actions.createFlow(safeUpdateObj);
      } else {
        res = await actions.updateFlow(flowId, safeUpdateObj);
      }

      if ((res?.status === 201 && !flowId) || (res?.status === 200 && flowId)) {
        showSnackBar(
          flowId
            ? 'Flow Updated Successfully'
            : `Flow ${updateObj.flow_id ? 'Clone' : 'Created'} Successfully`,
          'success'
        );

        setTableState((prevState) => {
          const currentList = prevState.cache[filterKey] || [];

          if (flowId) {
            const updatedList = currentList.map((flow) =>
              flow._id === flowId ? { ...updateObj, _id: flowId } : flow
            );
            return {
              ...prevState,
              cache: { ...prevState.cache, [filterKey]: updatedList },
            };
          } else {
            const newFlow = { ...updateObj, _id: res?.data?.id };
            const updatedList = [newFlow, ...currentList];
            return {
              ...prevState,
              cache: { ...prevState.cache, [filterKey]: updatedList },
            };
          }
        });

        onClose();
      } else {
        showSnackBar(
          flowId
            ? 'Flow Not Updated Successfully'
            : `Flow ${updateObj.flow_id ? 'Cloning' : 'Creation'} failed`,
          'error'
        );
      }
    } catch (error: any) {
      showSnackBar('Something went wrong. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreTemplates = async () => {
    if (templateFilters?.page < templateList?.pages) {
      setTemplateIsLoading(true);
      let listData = Array.isArray(templateList?.list) ? [...templateList?.list] : [];
      const data = { ...templateFilters, search: '', page: templateFilters.page + 1 };
      setTemplateFilters(data);

      try {
        const res = await templateActions.getWhatsappTemplateList(data);
        if (Array.isArray(res?.list)) {
          listData = [...listData, ...res.list];
          setTemplateList({ ...res, list: listData });
        } else {
          console.warn('Received non-array response for Template list');
        }
      } catch (error) {
        console.error('Error loading more templates:', error);
      } finally {
        setTemplateIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        patterns: [],
        tags: [],
        is_published: true,
        is_deleted: false,
        is_default: false,
        waba_id: '',
        phone_number_id: '',
        company_id: '',
        organisation_name: '',
        leads_form_name: '',
        app_name: '',
        campaign_id: '',
        campaign_name: '',
        lead_form_id: '',
        template_name: '',
        template_id: '',
        selectedCampaign: null,
        selectedForm: null,
        selectedOrganisation: null,
        selectedPhoneNumber: null,
        selectedTemplate: null,
        flow_type: 'normal',
      });
      setValidationErrors({
        organisation: '',
        phoneNumber: '',
        patterns: '',
        title: '',
      });
      setFormList([]);
      // setCampaignList({ pages: 0, list: [] });
      setTemplateList({ pages: 0, list: [{}] });
      // setChannelList([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (flowId && tableState?.cache[filterKey]?.length > 0) {
        const selectedFlow = tableState.cache[filterKey].find((flow) => flow._id === flowId);
        if (selectedFlow) {
          setFormData({
            ...formData,
            ...selectedFlow,
            selectedOrganisation: {
              name: selectedFlow.organisation_name,
              organisation_id: selectedFlow.company_id,
            },
            selectedCampaign: selectedFlow.campaign_id
              ? { label: selectedFlow.campaign_name || '', value: selectedFlow.campaign_id }
              : null,
            selectedForm: selectedFlow.lead_form_id
              ? { label: selectedFlow.leads_form_name || '', value: selectedFlow.lead_form_id }
              : null,
            selectedPhoneNumber: selectedFlow.phone_number_id
              ? {
                app_name: selectedFlow.app_name || '',
                wa_phone_number: selectedFlow.phone_number_id,
                label: selectedFlow.phone_number_id,
                value: selectedFlow.phone_number_id,
              }
              : null,
            selectedTemplate: selectedFlow.template_id
              ? { name: selectedFlow.template_name, wa_Id: selectedFlow.template_id }
              : null,
          });
        }
      } else if (cloneFrom) {
        // ✅ Handle clone modal data load
        setFormData({
          ...cloneFrom,
          title: `${cloneFrom.title} (Copy)`,
          patterns: [],
          selectedOrganisation: {
            name: cloneFrom.organisation_name,
            organisation_id: cloneFrom.company_id,
          },
          selectedCampaign: cloneFrom.campaign_id
            ? { label: cloneFrom.campaign_name || '', value: cloneFrom.campaign_id }
            : null,
          selectedForm: cloneFrom.lead_form_id
            ? { label: cloneFrom.leads_form_name || '', value: cloneFrom.lead_form_id }
            : null,
          selectedPhoneNumber: cloneFrom.phone_number_id
            ? {
              app_name: cloneFrom.app_name || '',
              wa_phone_number: cloneFrom.phone_number_id,
              label: cloneFrom.phone_number_id,
              value: cloneFrom.phone_number_id,
            }
            : null,
          selectedTemplate: cloneFrom.template_id
            ? { name: cloneFrom.template_name, wa_Id: cloneFrom.template_id }
            : null,
        });
      }
    }
  }, [open, flowId, cloneFrom, tableState]);

  const handleFlowTypeChange = (value) => {
    if (value === 'jump_bot') {
      setFormData((prev) => ({
        ...prev,
        flow_type: value,
        patterns: ['inside flow - keyword found_md_7'],
        // ✅ Clear campaign/form
        campaign_id: '', campaign_name: '', lead_form_id: '',
        leads_form_name: '', selectedCampaign: null, selectedForm: null,
      }));
    } else if (value === 'sorry_bot') {
      setFormData((prev) => ({
        ...prev,
        flow_type: value,
        patterns: ['neither valid input nor keyword_md_7 '],
        campaign_id: '', campaign_name: '', lead_form_id: '',
        leads_form_name: '', selectedCampaign: null, selectedForm: null,
      }));
    } else if (value === 'opt_out') {
      setFormData((prev) => ({
        ...prev,
        flow_type: value,
        patterns: ['unsubscribe, opt out,stop,optout '],
        campaign_id: '', campaign_name: '', lead_form_id: '',
        leads_form_name: '', selectedCampaign: null, selectedForm: null,
      }));
    } else if (value === 'opt_in') {
      setFormData((prev) => ({
        ...prev,
        flow_type: value,
        patterns: ['subscribe, opt in,optin,start'],
        campaign_id: '', campaign_name: '', lead_form_id: '',
        leads_form_name: '', selectedCampaign: null, selectedForm: null,
      }));
    } else {
      // normal
      setFormData((prev) => ({
        ...prev,
        flow_type: value,
        patterns: [],
      }));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: '8px',
          p: 3,
          width: 400,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {modalTitle}
        </Typography>
        <OrganisationDropdown
          label={
            <Typography component="span">
              Organization <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          value={formData?.selectedOrganisation || null}
          onChange={(value) => {
            onSelectOrganisation(value);
            setValidationErrors({ ...validationErrors, organisation: '' }); // Clear error for organization
          }}
          error={!!validationErrors?.organisation}
          errorMessage={validationErrors?.organisation}
          sx={{ mb: 1, mt: 2 }}
          autoSelectValue={false}
        />
        <SearchableDropdown
          label="Type"
          options={flowTypeOptions}
          value={
            flowTypeOptions.find(
              (item) => item.value === formData?.flow_type
            ) || null
          }
          onChange={(value) => {
            handleFlowTypeChange(value?.value);
          }}
          sx={{ mb: 1 }}
        />
        {/* Show Campaign Dropdown if Organization is selected */}
        {formData?.company_id && formData?.flow_type === 'normal' && (
          <CampaignDropdown
            value={
              formData?.selectedCampaign
                ? {
                  label: formData.campaign_name,
                  value: formData.campaign_id,
                  proposal_id: formData.campaign_id,
                  name: formData.campaign_name,
                }
                : null
            }
            onChange={(value) => {
              onSelectCampaign(value);
            }}
            organisationId={formData?.company_id || ''}
            error={false}
            errorMessage=""
            label="Business Services"
            disabled={false}
            sx={{ mb: 1 }}
          />
        )}

        {formData?.campaign_id && (
          <SearchableDropdown
            label="Form"
            options={formList?.map((form) => ({
              ...form,
              label: form?.leads_form_name || '',
              value: form?.leads_form_id || '',
            }))}
            value={formData?.selectedForm || null}
            onChange={async (value) => {
              let checkForm = await checkFormInFlow();
              if (checkForm) {
                setFormData({
                  ...formData,
                  lead_form_id: value?.leads_form_id?.toString() || '',
                  leads_form_name: value?.leads_form_name || '',
                  selectedForm: value,
                });
              }
            }}
            loading={loading}
            sx={{ mb: 1 }}
          />
        )}

        {formData?.company_id && (
          <BotNumberDropdown
            organisation={formData?.selectedOrganisation || null}
            onChange={(value) => {
              onSelectBusinessNumber(value);
              setValidationErrors({ ...validationErrors, phoneNumber: '' });
            }}
            value={formData?.selectedPhoneNumber}
            label={
              <Typography component="span">
                Phone Number <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            error={!!validationErrors?.phoneNumber}
            errorMessage={validationErrors?.phoneNumber}
            sx={{ mb: 1 }}
          // disabled={false}
          />
        )}

        {formData?.phone_number_id && (
          <SearchableDropdown
            label="Template"
            options={templateList?.list}
            value={formData?.selectedTemplate || null}
            onSearch={handleSearchTemplate}
            onChange={(value) => {
              setFormData({
                ...formData,
                template_name: value?.name,
                template_id: value?.wa_Id,
                selectedTemplate: value,
              });
            }}
            getOptionLabel={(option) => option?.name}
            fetchMoreOptions={templateList?.has_next ? loadMoreTemplates : undefined}
            loading={isTemplateloading}
            sx={{ mb: 1 }}
          />
        )}

        <TextField
          label={
            <Typography component="span">
              Flow Name <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          fullWidth
          value={formData?.title || ''}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            setValidationErrors({ ...validationErrors, title: '' }); // Clear error
          }}
          sx={{ mb: 2 }}
          margin="dense"
          variant="outlined"
          size="small"
          error={!!validationErrors?.title}
          helperText={validationErrors?.title}
        />
        <TextField
          label={
            <Typography component="span">
              Keywords (comma separated) <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          fullWidth
          disabled={
            formData?.flow_type === 'jump_bot' ||
            formData?.flow_type === 'sorry_bot' ||
            formData?.flow_type === 'opt_out' ||
            formData?.flow_type === 'opt_in'
          }
          value={(formData?.patterns || []).join(',')}
          onChange={(e) => {
            setFormData({
              ...formData,
              patterns: e.target.value.split(','),
            });
          }}
          onBlur={(e) => {
            const rawInput = e.target.value;

            const formattedKeywords = rawInput
              .split(',')
              .map((kw) => kw.replace(/\s+/g, ' ').trim())
              .filter((kw) => kw);

            setFormData({
              ...formData,
              patterns: formattedKeywords,
            });
          }}
          error={!!validationErrors?.patterns}
          helperText={validationErrors?.patterns}
          sx={{ mb: 2 }}
          margin="dense"
          variant="outlined"
          size="small"
        />
        <TextField
          label="Tags (comma separated)"
          fullWidth
          value={formData?.tags?.join(',') || ''}
          onChange={(e) => {
            const stringToArray = e?.target?.value?.split(',').map((item) => item.trim());
            setFormData({ ...formData, tags: stringToArray || [] });
          }}
          sx={{ mb: 2 }}
          margin="dense"
          variant="outlined"
          size="small"
        />

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose} size="small">
            Cancel
          </Button>
          <LoadingButton
            onClickButton={handleCreateOrUpdateFlow}
            isLoading={isloading}
            buttonName={`${modalTitle === 'Add A New Flow'
              ? 'Create Flow'
              : modalTitle === 'Copy Flow'
                ? 'Copy Flow'
                : 'Update Flow'
              }`}
          />
        </Box>
      </Box>
    </Modal>
  );
};
export default CreateEditModal;
